import { createActionCreator } from 'deox';
import {
  EntityForm,
  AppInfoForm,
  RateAssetForm,
  ArticleForm,
  CategoryForm,
} from '@/services/service-directory';
import {
  Entity,
  AppsInfo,
  Categories,
  Paging,
  AssetsSocial,
  ComplianceLevel,
  UserSocial,
  Article,
  DocOverview,
  Category,
  FeatureItems,
} from '@/types';

export const fetchEntityActions = {
  request: createActionCreator('FETCH_ENTITY_REQUEST', (resolve) => (subdomain: string) =>
    resolve({ subdomain }),
  ),
  success: createActionCreator('FETCH_ENTITY_SUCCESS', (resolve) => (entity: Entity) =>
    resolve(entity),
  ),
  failure: createActionCreator('FETCH_ENTITY_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const fetchEntityByIdActions = {
  request: createActionCreator('FETCH_ENTITY_BY_ID_REQUEST', (resolve) => (entityId: string) =>
    resolve({ entityId }),
  ),
  success: createActionCreator('FETCH_ENTITY_BY_ID_SUCCESS', (resolve) => (entity: Entity) =>
    resolve(entity),
  ),
  failure: createActionCreator('FETCH_ENTITY_BY_ID_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const createOrUpdateEntityActions = {
  request: createActionCreator(
    'CREATE_OR_UPDATE_ENTITY_REQUEST',
    (resolve) => (
      organisationId: string,
      entity: EntityForm,
      getDefaultApis?: boolean,
      optionalLink?: string,
    ) => resolve({ organisationId, entity, getDefaultApis, optionalLink }),
  ),
  success: createActionCreator('CREATE_OR_UPDATE_ENTITY_SUCCESS'),
  failure: createActionCreator('CREATE_OR_UPDATE_ENTITY_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchArticlesBySlugActions = {
  request: createActionCreator('FETCH_ARTICLES_BY_SLUG_REQUEST', (resolve) => (slug: string) =>
    resolve({ slug }),
  ),
  success: createActionCreator('FETCH_ARTICLES_BY_SLUG_SUCCESS', (resolve) => (article: Article) =>
    resolve(article),
  ),
  failure: createActionCreator('FETCH_ARTICLES_BY_SLUG_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchArticlesActions = {
  request: createActionCreator(
    'FETCH_ARTICLES_REQUEST',
    (resolve) => (
      organisationId: string | undefined,
      isGlobal: boolean,
      pageNum: number,
      pageSize: number,
      searchText?: string,
    ) => resolve({ organisationId, isGlobal, pageNum, pageSize, searchText }),
  ),
  success: createActionCreator(
    'FETCH_ARTICLES_SUCCESS',
    (resolve) => ({ data, paging }: { data: Article[]; paging: Paging }) =>
      resolve({ data, paging }),
  ),
  failure: createActionCreator('FETCH_ARTICLES_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const loadMoreArticlesActions = {
  request: createActionCreator(
    'LOAD_MORE_ARTICLES_REQUEST',
    (resolve) => (
      organisationId: string | undefined,
      isGlobal: boolean,
      pageNum: number,
      pageSize: number,
      searchText?: string,
    ) => resolve({ organisationId, isGlobal, pageNum, pageSize, searchText }),
  ),
  success: createActionCreator(
    'LOAD_MORE_ARTICLES_SUCCESS',
    (resolve) => ({ data, paging }: { data: Article[]; paging: Paging }) =>
      resolve({ data, paging }),
  ),
  failure: createActionCreator('LOAD_MORE_ARTICLES_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const createArticleActions = {
  request: createActionCreator(
    'CREATE_ARTICLE_REQUEST',
    (resolve) => (article: ArticleForm, isGlobal: boolean) => resolve({ article, isGlobal }),
  ),
  success: createActionCreator('CREATE_ARTICLE_SUCCESS'),
  failure: createActionCreator('CREATE_ARTICLE_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const editArticleActions = {
  request: createActionCreator(
    'EDIT_ARTICLE_REQUEST',
    (resolve) => (id: string, article: ArticleForm, isGlobal: boolean) =>
      resolve({ id, article, isGlobal }),
  ),
  success: createActionCreator('EDIT_ARTICLE_SUCCESS'),
  failure: createActionCreator('EDIT_ARTICLE_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const deleteArticleActions = {
  request: createActionCreator(
    'DELETE_ARTICLE_REQUEST',
    (resolve) => (id: string, isGlobal: boolean) => resolve({ id, isGlobal }),
  ),
  success: createActionCreator('DELETE_ARTICLE_SUCCESS'),
  failure: createActionCreator('DELETE_ARTICLE_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchAppsInfoActions = {
  request: createActionCreator('FETCH_APPS_INFO_REQUEST', (resolve) => (organisationId: string) =>
    resolve({ organisationId }),
  ),
  success: createActionCreator('FETCH_APPS_INFO_SUCCESS', (resolve) => (appsInfo: AppsInfo) =>
    resolve(appsInfo),
  ),
  failure: createActionCreator('FETCH_APPS_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const createAppInfoActions = {
  request: createActionCreator('CREATE_APP_INFO_REQUEST', (resolve) => (appInfo: AppInfoForm) =>
    resolve({ appInfo }),
  ),
  success: createActionCreator('CREATE_APP_INFO_SUCCESS'),
  failure: createActionCreator('CREATE_APP_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const editAppInfoActions = {
  request: createActionCreator(
    'EDIT_APP_INFO_REQUEST',
    (resolve) => (id: string, appInfo: AppInfoForm) => resolve({ id, appInfo }),
  ),
  success: createActionCreator('EDIT_APP_INFO_SUCCESS'),
  failure: createActionCreator('EDIT_APP_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const deleteAppInfoActions = {
  request: createActionCreator('DELETE_APP_INFO_REQUEST', (resolve) => (id: string) =>
    resolve({ id }),
  ),
  success: createActionCreator('DELETE_APP_INFO_SUCCESS'),
  failure: createActionCreator('DELETE_APP_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const scrapeAppInfoActions = {
  request: createActionCreator(
    'SCRAPE_APP_INFO_REQUEST',
    (resolve) => (id: string, playStoreLink?: string, appStoreLink?: string) =>
      resolve({ id, playStoreLink, appStoreLink }),
  ),
  success: createActionCreator('SCRAPE_APP_INFO_SUCCESS'),
  failure: createActionCreator('SCRAPE_APP_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const createAndScrapeAppInfoActions = {
  request: createActionCreator('CREATE_SCRAPE_APP_INFO_REQUEST', (resolve) => (urlScrape: string) =>
    resolve({ urlScrape }),
  ),
  success: createActionCreator('CREATE_SCRAPE_APP_INFO_SUCCESS'),
  failure: createActionCreator('CREATE_SCRAPE_APP_INFO_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchCategoriesActions = {
  request: createActionCreator(
    'FETCH_CATEGORIES_REQUEST',
    (resolve) => (entryType?: string, industry?: string) => resolve({ entryType, industry }),
  ),
  success: createActionCreator('FETCH_CATEGORIES_SUCCESS', (resolve) => (categories: Categories) =>
    resolve(categories),
  ),
  failure: createActionCreator('FETCH_CATEGORIES_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchCategoryByIdActions = {
  request: createActionCreator('FETCH_CATEGORY_REQUEST', (resolve) => (id: string) =>
    resolve({ id }),
  ),
  success: createActionCreator('FETCH_CATEGORY_SUCCESS', (resolve) => (category: Category) =>
    resolve(category),
  ),
  failure: createActionCreator('FETCH_CATEGORY_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const createCategoryActions = {
  request: createActionCreator(
    'CREATE_CATEGORY_REQUEST',
    (resolve) => (categoryInfo: CategoryForm) => resolve({ categoryInfo }),
  ),
  success: createActionCreator('CREATE_CATEGORY_SUCCESS'),
  failure: createActionCreator('CREATE_CATEGORY_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const editCategoryActions = {
  request: createActionCreator(
    'EDIT_APP_INFO_REQUEST',
    (resolve) => (id: string, categoryInfo: CategoryForm) => resolve({ id, categoryInfo }),
  ),
  success: createActionCreator('EDIT_CATEGORY_SUCCESS'),
  failure: createActionCreator('EDIT_CATEGORY_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const deleteCategoryActions = {
  request: createActionCreator(
    'DELETE_CATEGORY_REQUEST',
    (resolve) => (id: string, entryType: string) => resolve({ id, entryType }),
  ),
  success: createActionCreator('DELETE_CATEGORY_SUCCESS'),
  failure: createActionCreator('DELETE_CATEGORY_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const rateEntityActions = {
  request: createActionCreator(
    'RATE_ENTITY_DOC_REQUEST',
    (resolve) => (id: string, rating: number) => resolve({ id, rating }),
  ),
  success: createActionCreator('RATE_ENTITY_DOC_SUCCESS'),
  failure: createActionCreator('RATE_ENTITY_DOC_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const rateAssetActions = {
  request: createActionCreator(
    'RATE_ASSET_REQUEST',
    (resolve) => (formAsset: RateAssetForm, socialUserToken: string) =>
      resolve({ formAsset, socialUserToken }),
  ),
  success: createActionCreator('RATE_ASSET_SUCCESS'),
  failure: createActionCreator('RATE_ASSET_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchAssetSocialActions = {
  request: createActionCreator(
    'FETCH_ASSET_SOCIAL_REQUEST',
    (resolve) => (socialUserToken: string) => resolve({ socialUserToken }),
  ),
  success: createActionCreator('FETCH_ASSET_SOCIAL_SUCCESS', (resolve) => (data: AssetsSocial) =>
    resolve(data),
  ),
  failure: createActionCreator('FETCH_ASSET_SOCIAL_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};

export const fetchListUserSocialActions = {
  request: createActionCreator(
    'FETCH_LIST_USER_SOCIAL_REQUEST',
    (resolve) => (pageSize: number, pageNum: number, sort?: string, searchText?: string) =>
      resolve({ pageNum, pageSize, sort, searchText }),
  ),
  success: createActionCreator(
    'FETCH_LIST_USER_SOCIAL_SUCCESS',
    (resolve) => ({ data, paging }: { data: UserSocial[]; paging: Paging }) =>
      resolve({ data, paging }),
  ),
  failure: createActionCreator('FETCH_LIST_USER_SOCIAL_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const fetchListEntityActions = {
  request: createActionCreator(
    'FETCH_LIST_ENTITY_REQUEST',
    (resolve) => (pageNum: number, pageSize: number, searchEntity: string) =>
      resolve({ pageNum, pageSize, searchEntity }),
  ),
  success: createActionCreator(
    'FETCH_LIST_ENTITY_SUCCESS',
    (resolve) => ({ data, paging }: { data: Entity[]; paging: Paging }) =>
      resolve({ data, paging }),
  ),
  failure: createActionCreator('FETCH_LIST_ENTITY_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const deleteEntityActions = {
  request: createActionCreator('DELETE_ENTITY_REQUEST', (resolve) => (entityId: string) =>
    resolve({ entityId }),
  ),
  success: createActionCreator('DELETE_ENTITY_SUCCESS', (resolve) => (entityId: string) =>
    resolve({ entityId }),
  ),
  failure: createActionCreator('DELETE_ENTITY_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const fetchComplianceLevelsActions = {
  request: createActionCreator('FETCH_COMPLIANCE_LEVELS_REQUEST', (resolve) => () => resolve()),
  success: createActionCreator(
    'FETCH_COMPLIANCE_LEVELS_SUCCESS',
    (resolve) => (data: ComplianceLevel[]) => resolve({ data }),
  ),
  failure: createActionCreator('FETCH_COMPLIANCE_LEVELS_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const generateIndexesActions = {
  request: createActionCreator('GENERATE_INDEXES_REQUEST'),
  success: createActionCreator('GENERATE_INDEXES_SUCCESS'),
  failure: createActionCreator('GENERATE_INDEXES_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const fetchDocsOverviewActions = {
  request: createActionCreator(
    'FETCH_DOCS_OVERVIEW_REQUEST',
    (resolve) => (organisationId: string) => resolve({ organisationId }),
  ),
  success: createActionCreator('FETCH_DOCS_OVERVIEW_SUCCESS', (resolve) => (data: DocOverview[]) =>
    resolve({ data }),
  ),
  failure: createActionCreator('FETCH_DOCS_OVERVIEW_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const updateDocOverviewActions = {
  request: createActionCreator(
    'UPDATE_DOC_OVERVIEW_REQUEST',
    (resolve) => (organisationId: string, tags: string, docString: string) =>
      resolve({ organisationId, tags, docString }),
  ),
  success: createActionCreator('UPDATE_DOC_OVERVIEW_SUCCESS', (resolve) => () => resolve()),
  failure: createActionCreator('UPDATE_DOC_OVERVIEW_FAILURE', (resolve) => (error: any) =>
    resolve({ error }),
  ),
};

export const getFeatureItemsActions = {
  request: createActionCreator('GET_FEATURE_ITEMS_REQUEST'),
  success: createActionCreator('GET_FEATURE_ITEMS_SUCCESS', (resolve) => (data: FeatureItems) =>
    resolve(data),
  ),
  failure: createActionCreator('GET_FEATURE_ITEMS_FAILURE', (resolve) => (error: unknown) =>
    resolve({ error }),
  ),
};
